import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks";
import { productPrvSliceActions } from "../store/slices/productSlice";
import { IProduct } from "../types/product";
import PreviousIcon from "../assets/icons/PreviousIcon";
import NextIcon from "../assets/icons/NextIcon";
import CartLargeIcon from "../assets/icons/CartLargeIcon";
import MinusIcon from "../assets/icons/MinusIcon";
import PlusIcon from "../assets/icons/PlusIcon";
import { addProductToCart, addProductToCartWithQuantity, clearProductInCart, removeProductInCart, updateQuantityProductInCart } from "../store/slices/cartSlice";
import { setMetaData } from "../helpers/handleSEO";
import { categoryPrvSliceActions } from "../store/slices/categorySlice";
import SlideProductOther from "../components/slides/SlideProductOther";
import Cart from "../components/layouts/main/Cart";
import { getCartState, getProductState } from "../store";
import PlayVideoIcon from "../assets/icons/PlayVideoIcon";
import { formatProductInCart, getProductUnitPriceByQuantity } from "../helpers/handleProduct";
import { formatHTMLString } from "../helpers/handleString";

const ProductPage = () => {
    const { slug } = useParams();
    const dispatch = useAppDispatch();
    const { cartInfo } = useAppSelector(getCartState);
    const { productStore } = useAppSelector(getProductState);
    const [product, setProduct] = useState<IProduct>({} as IProduct);
    const [products, setProducts] = useState<IProduct[]>([]);
    const [indexImg, setIndexImg] = useState<number>(0);
    const [videoShow, setVideoShow] = useState<string>("");
    useEffect(() => {
        if (productStore.length > 0) return;
        dispatch(productPrvSliceActions.getProductList());
    }, [dispatch, productStore.length]);
    useEffect(() => {
        const fetchProductDetail = async () => {
            const res = await dispatch(productPrvSliceActions.getProductDetail(slug));
            const productDetail = (res.payload as any).data;
            const categories = (res.payload as any).categories;
            if (categories) {
                for (let i = 0; i < categories.length; i++) {
                    const category = categories[i];
                    const categoryId = category.category_id;
                    if (categoryId) {
                        const productsByCategory = await dispatch(categoryPrvSliceActions.getProductsByCategory(categoryId));
                        if (productsByCategory.payload) {
                            const productsData = (productsByCategory.payload as any).data;
                            const productList = productsData
                                .filter((product: IProduct) => product.id !== productDetail.id);
                            setProducts(prev => {
                                const newProducts = productList.filter((p: IProduct) => !prev.some((prevP: IProduct) => prevP.id === p.id));
                                return [...prev, ...newProducts]
                            });
                        }
                    }
                }
            }
            setProduct(prev => prev = productDetail);
            setMetaData({ title: productDetail.name, description: productDetail.description || '' });
        }
        fetchProductDetail();
    }, [slug, dispatch])
    const productFromStore = useMemo(() => {
        if (!slug) return undefined;
        return productStore.find(item => item.slug === slug);
    }, [productStore, slug]);
    const displayProduct = useMemo(() => {
        if (!productFromStore) return product;
        const detailPrice = (product as unknown as { price?: number })?.price;
        return {
            ...productFromStore,
            ...product,
            price_sale: product.price_sale ?? detailPrice ?? productFromStore.price_sale,
            unit_price: product.unit_price ?? productFromStore.unit_price,
            wholesale_upto: product.wholesale_upto ?? productFromStore.wholesale_upto,
            wholesale_price: product.wholesale_price?.length
                ? product.wholesale_price
                : productFromStore.wholesale_price,
        } as IProduct;
    }, [product, productFromStore]);
    const productInCart = useMemo(() => {
        return cartInfo.find(item => item.id === displayProduct.id);
    }, [cartInfo, displayProduct.id])
    const displayQuantity = useMemo(() => (productInCart ? productInCart.quantity : 1), [productInCart]);
    const wholesaleUnitPrice = useMemo(() => {
        return getProductUnitPriceByQuantity(displayProduct, displayQuantity);
    }, [displayProduct, displayQuantity]);
    const showWholesalePrice = displayProduct.wholesale_upto > 0 && wholesaleUnitPrice < Number(displayProduct.price_sale);
    return (
        <div className={`bg-[#F4F4F4] p-4 w-full min-h-screen`}>
            <div className="h-fit flex gap-2 mx-auto max-w-[1200px]">
                <div className="bg-white p-6 rounded-lg w-2/3 max-md:w-full">
                    <div className="flex gap-12 max-md:flex-col">
                        <div>
                            <div className="relative flex-1">
                                <button
                                    className="absolute left-0 top-1/2 -translate-y-1/2 bg-white p-1 rounded-full disabled:opacity-70"
                                    disabled={indexImg === 0}
                                    onClick={() => {
                                        if (indexImg === 0) return;
                                        setIndexImg(prev => prev - 1);
                                    }}
                                >
                                    <PreviousIcon />
                                </button>
                                {displayProduct
                                    && !videoShow
                                    && displayProduct.photos
                                    && displayProduct.photos.length > 0
                                    && displayProduct.photos.slice(indexImg, indexImg + 1).map((img, index) =>
                                        <img key={index} src={img} alt="product" className="object-contain aspect-square" />
                                    )
                                }
                                {videoShow && <iframe
                                    src={videoShow}
                                    style={{ aspectRatio: "16/9", width: "100%", height: "100%" }}
                                    allowFullScreen
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                >
                                </iframe>
                                }
                                <button
                                    className="absolute right-0 top-1/2 -translate-y-1/2 bg-white p-1 rounded-full disabled:opacity-70"
                                    disabled={!!displayProduct.photos && indexImg === displayProduct.photos.length - 1}
                                    onClick={() => {
                                        if (!displayProduct.photos) return;
                                        if (indexImg === displayProduct.photos.length - 1) return;
                                        setIndexImg(prev => prev + 1);
                                        setVideoShow(prev => prev = "");
                                    }}
                                >
                                    <NextIcon />
                                </button>
                            </div>
                            <div className="flex gap-3 mt-4 flex-wrap">
                                {displayProduct.photos
                                    && displayProduct.photos.length > 0
                                    && displayProduct.photos.map((img, index) =>
                                        <button
                                            key={index}
                                            className={`size-[90px] overflow-hidden ${indexImg === index && !videoShow ? 'border-2 border-blue-600' : 'border-2 border-white'}`}
                                            onClick={() => {
                                                setIndexImg(prev => prev = index);
                                                setVideoShow(prev => prev = "");
                                            }}
                                        >
                                            <img src={img} alt="product" className="w-full h-full object-contain" />
                                        </button>
                                    )
                                }
                                {displayProduct.video_url &&
                                    <button
                                        className={`flex justify-center items-center size-20 ${videoShow && "border-blue-600 border-2 border-solid"}`}
                                        onClick={() => {
                                            setVideoShow(prev => prev = displayProduct.video_url.replace("https://youtu.be", "https://youtube.com/embed"));
                                            setIndexImg(prev => prev = 0);
                                        }}
                                    >
                                        <PlayVideoIcon />
                                    </button>}

                            </div>
                        </div>
                        <div className="flex flex-col gap-6 flex-1">
                            <h1 className="text-2xl font-bold">{displayProduct.name}</h1>
                            <div className="flex items-end gap-5">
                                <p className="text-xl font-bold text-red-600">
                                    {(showWholesalePrice ? wholesaleUnitPrice : displayProduct.price_sale)?.toLocaleString("vi-VN")}đ
                                </p>
                                {showWholesalePrice ? (
                                    <p className="text-base font-normal line-through">{displayProduct.price_sale?.toLocaleString("vi-VN")}đ</p>
                                ) : (
                                    displayProduct.price_sale < displayProduct.unit_price &&
                                        <>
                                            <p className="text-base font-normal line-through">{displayProduct.unit_price?.toLocaleString("vi-VN")}đ</p>
                                            <p className="text-sm font-normal px-2 py-1 bg-red-700 text-white text-center rounded">
                                                {Math.round(100 - ((displayProduct.price_sale / displayProduct.unit_price) * 100))}%
                                            </p>
                                        </>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                {productInCart && <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => {
                                            dispatch(removeProductInCart(displayProduct.id));
                                        }}
                                    >
                                        <MinusIcon />
                                    </button>
                                    <input
                                        type="text"
                                        value={productInCart.quantity}
                                        className="w-20 text-center outline-none border-b-2 border-gray-300 border-solid bg-inherit"
                                        onChange={(ev) => {
                                            const value = +ev.target.value;
                                            dispatch(
                                                updateQuantityProductInCart({
                                                    id: displayProduct.id,
                                                    quantity: value,
                                                })
                                            );
                                        }}
                                        onBlur={(ev) => {
                                            const value = +ev.target.value;
                                            if (!value) {
                                                return dispatch(clearProductInCart({ id: displayProduct.id }));
                                            }
                                        }}
                                    />
                                    <button onClick={() => {
                                        dispatch(addProductToCart(formatProductInCart(displayProduct, "product")));
                                    }}>
                                        <PlusIcon />
                                    </button>
                                </div>}
                                {!productInCart && <button
                                    className="bg-[#0088FF] flex items-center gap-1 max-w-fit px-3 py-2 rounded-md"
                                    onClick={() => {
                                        dispatch(
                                            addProductToCartWithQuantity({
                                                quantity: 1,
                                                product: formatProductInCart(displayProduct, "product"),
                                            })
                                        );
                                    }}
                                >
                                    <CartLargeIcon />
                                    <p className="text-white text-base font-normal">Thêm vào giỏ</p>
                                </button>}
                            </div>
                        </div>
                    </div>
                    <div className="my-4 w-full">
                        {displayProduct.description && <div
                            id="product-description"
                            dangerouslySetInnerHTML={{ __html: formatHTMLString(displayProduct.description) }}>
                        </div>
                        }
                        {displayProduct.content &&
                            <div
                                id="product-content"
                                dangerouslySetInnerHTML={{ __html: formatHTMLString(displayProduct.content) }}>
                            </div>
                        }
                    </div>
                </div>
                <div className="w-1/3 max-md:hidden">
                    <Cart />
                </div>
            </div>
            {products && products.length > 0 && <SlideProductOther products={products} />}
        </div >
    )
}
export default ProductPage;